import { useGetFeatureFlagsQuery } from "@/redux/apis/featureFlag/featureFlagApi";

/**
 * Whether a stored flag value means "on".
 *
 * Values reach us from JSON columns, so the same flag can arrive as `true` or
 * `"true"` or `"1"` depending on who last wrote it. Anything not recognisably
 * true is off — an unset flag (null) included.
 */
const isTruthyFlagValue = (value: string | number | boolean | null | undefined): boolean =>
  value === true || value === "true" || value === "1" || value === 1;

export interface FeatureFlagResult {
  /** The resolved value. False while the query is in flight — pair it with
   *  `isLoading` whenever "off" and "not known yet" must be told apart. */
  enabled: boolean;
  isLoading: boolean;
}

/**
 * Read one of the operator's `ff-*` subscription feature flags.
 *
 * The whole set is fetched once and cached, so calling this in several
 * components costs one request. A provider with no valid subscription has no
 * flags at all, which reads as off — the same answer, without a special case.
 *
 * `enabled` is false before the query resolves. That is the safe default for
 * rendering, but it is NOT the same as "off": anything that would start work
 * on the strength of it — a mount effect, a paid call — must wait on
 * `isLoading` first.
 */
export const useFeatureFlag = (slug: string): FeatureFlagResult => {
  const { data, isLoading } = useGetFeatureFlagsQuery();

  const flag = data?.featureFlags.find((f) => f.slug === slug);

  return { enabled: isTruthyFlagValue(flag?.value), isLoading };
};
